import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Row {
  id: string;
  redeemed_at: string;
  user_id: string;
  ip: string | null;
  user_agent: string | null;
  code_id: string;
  code: string | null;
  label: string | null;
  app: string | null;
  granted_tier: string | null;
  granted_alacarte: string[] | null;
  display_name: string | null;
  email: string | null;
}

const csvField = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.split('"').join('""')}"` : s;
};

const InfluencerRedemptionsPanel = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const { data: reds, error } = await supabase
      .from("influencer_code_redemptions")
      .select(`
        id, redeemed_at, user_id, ip, user_agent, code_id,
        influencer_access_codes!inner ( code, label, app, granted_tier, granted_alacarte )
      `)
      .order("redeemed_at", { ascending: false })
      .limit(500);

    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }

    const userIds = Array.from(new Set((reds ?? []).map((r) => r.user_id)));
    const profileMap = new Map<string, { display_name: string | null }>();
    if (userIds.length) {
      const { data: profs } = await supabase
        .from("public_profiles")
        .select("id, display_name")
        .in("id", userIds);
      for (const p of profs ?? []) {
        profileMap.set(p.id, { display_name: p.display_name });
      }
    }

    const flat: Row[] = (reds ?? []).map((r) => {
      const c = (r as { influencer_access_codes: { code: string; label: string | null; app: string; granted_tier: string; granted_alacarte: string[] } }).influencer_access_codes;
      const prof = profileMap.get(r.user_id);
      return {
        id: r.id,
        redeemed_at: r.redeemed_at,
        user_id: r.user_id,
        ip: r.ip,
        user_agent: r.user_agent,
        code_id: r.code_id,
        code: c?.code ?? null,
        label: c?.label ?? null,
        app: c?.app ?? null,
        granted_tier: c?.granted_tier ?? null,
        granted_alacarte: c?.granted_alacarte ?? null,
        display_name: prof?.display_name ?? null,
        email: null,
      };
    });
    setRows(flat);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.code, r.label, r.email, r.display_name, r.app, r.user_id].some((v) =>
        v?.toLowerCase().includes(q)
      )
    );
  }, [rows, filter]);

  const exportCsv = () => {
    const header = ["redeemed_at", "code", "label", "app", "tier", "alacarte", "user_id", "display_name", "email", "ip"];
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push([
        r.redeemed_at,
        r.code ?? "",
        r.label ?? "",
        r.app ?? "",
        r.granted_tier ?? "",
        (r.granted_alacarte ?? []).join("|"),
        r.user_id,
        r.display_name ?? "",
        r.email ?? "",
        r.ip ?? "",
      ].map(csvField).join(","));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `influencer-redemptions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uniqueRedeemers = useMemo(() => new Set(filtered.map((r) => r.user_id)).size, [filtered]);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4"><p className="text-xs text-muted-foreground">Redemptions</p><p className="text-2xl font-bold">{filtered.length}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Unique users</p><p className="text-2xl font-bold">{uniqueRedeemers}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">Last 24h</p><p className="text-2xl font-bold">{filtered.filter((r) => Date.now() - new Date(r.redeemed_at).getTime() < 86400_000).length}</p></Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by code, label, email, app…"
          className="max-w-xs"
        />
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      <Card className="divide-y">
        {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">No redemptions yet.</div>
        )}
        {filtered.map((r) => (
          <div key={r.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-bold tracking-[0.2em]">{r.code ?? "—"}</span>
                {r.label && <Badge variant="secondary">{r.label}</Badge>}
                {r.app && <Badge variant="outline">{r.app}</Badge>}
                {r.granted_tier && <Badge variant="outline">{r.granted_tier.replace("tier_", "")}</Badge>}
              </div>
              <span className="text-xs text-muted-foreground">
                {new Date(r.redeemed_at).toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {r.display_name ?? "—"}
              {r.email && ` · ${r.email}`}
              {" · "}
              <span className="font-mono">{r.user_id.slice(0, 8)}…</span>
              {r.ip && ` · ip ${r.ip}`}
            </p>
            {r.granted_alacarte && r.granted_alacarte.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {r.granted_alacarte.map((k) => (
                  <Badge key={k} variant="secondary" className="text-[10px]">{k}</Badge>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
};

export default InfluencerRedemptionsPanel;
